import IItemStub from '@/models/items/IItemStub';

type FetchBatch = (ids: number[]) => Promise<IItemStub[]>;

interface Waiter {
    resolve: (s: IItemStub | null) => void;
    reject: (e: unknown) => void;
}

/**
 * Collects ids requested during one microtask turn and issues a single batched
 * fetch per `maxBatch` chunk. Cards mount together, so without this each card
 * fires its own request (a 40-entry drop list was 40 requests).
 *
 * Ids missing from the response resolve to null rather than rejecting: the bulk
 * endpoint deliberately skips unknown ids so one bad id cannot fail a batch
 * assembled from many independent callers.
 */
export function createStubBatcher(fetchBatch: FetchBatch, maxBatch = 100): (id: number) => Promise<IItemStub | null> {
    let pending = new Map<number, Waiter[]>();
    let scheduled = false;

    const flush = async () => {
        const batch = pending;
        pending = new Map();
        scheduled = false;

        const ids = Array.from(batch.keys());
        for (let i = 0; i < ids.length; i += maxBatch) {
            const chunk = ids.slice(i, i + maxBatch);
            try {
                const rows = await fetchBatch(chunk);
                const byId = new Map(rows.map((r) => [r.id, r]));
                for (const id of chunk) {
                    const got = byId.get(id) ?? null;
                    for (const w of batch.get(id)!) {
                        w.resolve(got);
                    }
                }
            } catch (e) {
                for (const id of chunk) {
                    for (const w of batch.get(id)!) {
                        w.reject(e);
                    }
                }
            }
        }
    };

    return (id: number) =>
        new Promise<IItemStub | null>((resolve, reject) => {
            const waiters = pending.get(id);
            if (waiters) {
                waiters.push({ resolve, reject });
            } else {
                pending.set(id, [{ resolve, reject }]);
            }
            if (!scheduled) {
                scheduled = true;
                Promise.resolve().then(flush);
            }
        });
}
